/**
 * Translate Zod-level validation failures into a small list of
 * plain-language "problems" with a "what to do" hint each. The import
 * wizard shows up to ~6 problems in a ✗ checklist so non-technical
 * admins can self-correct without reading raw Zod output.
 */
import {
  exportEnvelopeSchema,
  EXPORT_SCHEMA_ID,
  SUPPORTED_EXPORT_VERSION,
  type ExportEnvelope,
} from './schema';

export interface ValidationProblem {
  message: string;
  hint: string;
}

/**
 * Result of validating a candidate export file. When `ok` is true, `envelope`
 * is populated; when `ok` is false, `problems` is populated.
 *
 * NOTE: This is modeled as a flat interface with optional fields rather than
 * a discriminated union because the project's `tsconfig.json` has
 * `strict: false`, which disables `strictNullChecks` and prevents TypeScript
 * from narrowing discriminated unions on boolean discriminators. The flat
 * shape preserves the intended `if (r.ok)` / `if (!r.ok)` usage pattern.
 */
export interface ValidationResult {
  ok: boolean;
  envelope?: ExportEnvelope;
  problems?: ValidationProblem[];
}

export function validateForUser(rawText: string): ValidationResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawText);
  } catch {
    return {
      ok: false,
      problems: [
        {
          message: 'This is not a Stellar agent export file.',
          hint: "Make sure you picked the .json file you downloaded from the Export button.",
        },
      ],
    };
  }

  const obj = parsed as Record<string, unknown> | null;

  if (
    !obj ||
    typeof obj !== 'object' ||
    obj.$schema !== EXPORT_SCHEMA_ID
  ) {
    return {
      ok: false,
      problems: [
        {
          message: 'This is not a Stellar agent export file.',
          hint: 'Re-export the agent and try again.',
        },
      ],
    };
  }

  if (
    typeof obj.exportVersion === 'number' &&
    obj.exportVersion > SUPPORTED_EXPORT_VERSION
  ) {
    return {
      ok: false,
      problems: [
        {
          message: 'This file was created by a newer version of Stellar Chat.',
          hint: 'Upgrade this environment before importing.',
        },
      ],
    };
  }

  const result = exportEnvelopeSchema.safeParse(obj);
  if (result.success) return { ok: true, envelope: result.data };

  const problems: ValidationProblem[] = result.error.issues
    .slice(0, 6)
    .map((issue) => ({
      message: `${issue.path.join('.') || 'agent'}: ${issue.message}`,
      hint: 'Re-export the agent from the source environment.',
    }));
  return { ok: false, problems };
}
